import { t } from "../../lib/sprache";

/**
 * Demo-Termine (0390): Zeitfenster, die der Betrieb freigibt und die Interessenten auf creatoroffice.de/demo buchen. Alle Zeiten
 * stehen in deutscher Zeit (Europe/Berlin), unabhängig vom Gerät; die Datenbank rechnet Datum und Uhrzeit selbst in UTC um.
 */

export const DEMO_ZONE = "Europe/Berlin";

export interface DemoTestOrg { id: string; name: string; status: string; trial_ends_at: string | null }
export interface DemoBooking {
  id: string; name: string; email: string; company: string; note: string | null; language: "de" | "de-CH" | "en";
  created_at: string; cancelled_at: string | null; cancelled_by: "bucher" | "betrieb" | null; rescheduled_at: string | null;
  ics_sequence: number; reminder_sent_at: string | null;
  slot: { id: string; starts_at: string; duration_minutes: number; meeting_url: string | null };
  previous_slot: { id: string; starts_at: string; duration_minutes: number } | null;
  test_org: DemoTestOrg | null;
}
export interface DemoCancelled { id: string; name: string; email: string; company: string; cancelled_at: string; cancelled_by: "bucher" | "betrieb" }
export interface DemoSlot {
  id: string; starts_at: string; duration_minutes: number; meeting_url: string | null; created_at: string; removed_at: string | null;
  booking: DemoBooking | null; cancelled: DemoCancelled[];
}
export interface DemoMark { org_id: string; booking_id: string; starts_at: string; name: string }

/** Eingabezeile im Formular „Zeitfenster freigeben“ (Datum und Uhrzeit in deutscher Zeit) */
export interface SlotDraft { day: string; time: string; minutes: number; link: string }

export const DAUERN = [15, 30, 45, 60, 90] as const;

const parts = (iso: string, opts: Intl.DateTimeFormatOptions, locale: string) =>
  new Intl.DateTimeFormat(locale, { timeZone: DEMO_ZONE, ...opts }).formatToParts(new Date(iso));
const part = (p: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === type)?.value ?? "";

/** Tag in deutscher Zeit als JJJJ-MM-TT */
export function berlinDay(iso: string): string {
  const p = parts(iso, { year: "numeric", month: "2-digit", day: "2-digit" }, "en-CA");
  return `${part(p, "year")}-${part(p, "month")}-${part(p, "day")}`;
}
/** Uhrzeit in deutscher Zeit als HH:MM */
export function berlinTime(iso: string): string {
  const p = parts(iso, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, "de-DE");
  return `${part(p, "hour")}:${part(p, "minute")}`;
}
/** „Dienstag, 27.10.2026“ bzw. „Tuesday, 27 Oct 2026“ */
export function dayLabel(iso: string): string {
  return new Intl.DateTimeFormat(t("de-DE", "en-GB"), { timeZone: DEMO_ZONE, weekday: "long", day: "2-digit", month: t("2-digit", "short") as "2-digit" | "short", year: "numeric" }).format(new Date(iso));
}
/** „10:00–10:30“ */
export function timeRange(iso: string, minutes: number): string {
  return `${berlinTime(iso)}–${berlinTime(new Date(Date.parse(iso) + minutes * 60_000).toISOString())}`;
}
/** „Di., 27.10., 10:00“ für kurze Hinweise */
export function shortWhen(iso: string): string {
  const d = new Intl.DateTimeFormat(t("de-DE", "en-GB"), { timeZone: DEMO_ZONE, weekday: "short", day: "2-digit", month: "2-digit" }).format(new Date(iso));
  return `${d}, ${berlinTime(iso)}`;
}

/** Kommende (Ende noch nicht vorbei) und vergangene Zeitfenster; kommende aufsteigend, vergangene absteigend */
export function splitSlots(slots: readonly DemoSlot[], nowMs: number): { kommend: DemoSlot[]; vergangen: DemoSlot[] } {
  const ende = (s: DemoSlot) => Date.parse(s.starts_at) + s.duration_minutes * 60_000;
  const kommend = slots.filter((s) => ende(s) > nowMs && !s.removed_at).sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
  const vergangen = slots.filter((s) => ende(s) <= nowMs || !!s.removed_at).sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at));
  return { kommend, vergangen };
}

/** Kommende Zeitfenster nach Tag gruppiert (deutsche Zeit), in Reihenfolge */
export function groupByDay(slots: readonly DemoSlot[]): { day: string; label: string; slots: DemoSlot[] }[] {
  const out: { day: string; label: string; slots: DemoSlot[] }[] = [];
  for (const s of slots) {
    const day = berlinDay(s.starts_at);
    const last = out[out.length - 1];
    if (last && last.day === day) last.slots.push(s);
    else out.push({ day, label: dayLabel(s.starts_at), slots: [s] });
  }
  return out;
}

export function slotStatus(s: DemoSlot): { label: string; tone: "success" | "info" | "neutral" | "warning"; symbol: string } {
  if (s.removed_at) return { label: t("Entfernt", "Removed"), tone: "neutral", symbol: "○" };
  if (s.booking) return { label: t("Gebucht", "Booked"), tone: "success", symbol: "✓" };
  return { label: t("Frei", "Open"), tone: "info", symbol: "◇" };
}

export function languageLabel(l: string): string {
  return l === "en" ? t("Englisch", "English") : l === "de-CH" ? t("Deutsch (Schweiz)", "German (Switzerland)") : t("Deutsch", "German");
}

/** Leere Zeile: nächster Werktag 10:00, 30 Minuten, Link folgt */
export function firstDraft(nowMs: number): SlotDraft {
  return { day: nextWorkday(berlinDay(new Date(nowMs).toISOString())), time: "10:00", minutes: 30, link: "" };
}
function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export function nextWorkday(day: string): string {
  let d = addDays(day, 1);
  while ([0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay())) d = addDays(d, 1);
  return d;
}
/** Nächstes Zeitfenster direkt im Anschluss (gleicher Tag, Start = Ende des vorigen, auf 15 Minuten aufgerundet) */
export function draftAfter(prev: SlotDraft): SlotDraft {
  const [h, m] = prev.time.split(":").map(Number) as [number, number];
  const ende = Math.ceil((h * 60 + m + prev.minutes) / 15) * 15;
  if (ende >= 24 * 60) return { ...prev, day: nextWorkday(prev.day) };
  return { ...prev, time: `${String(Math.floor(ende / 60)).padStart(2, "0")}:${String(ende % 60).padStart(2, "0")}` };
}
/** Gleiche Uhrzeit am nächsten Werktag */
export function draftNextDay(prev: SlotDraft): SlotDraft {
  return { ...prev, day: nextWorkday(prev.day) };
}

/** Prüft die Zeilen vor dem Senden; liefert Fehlertext je Zeile (leer = in Ordnung). Die Datenbank prüft noch einmal. */
export function draftProblems(rows: readonly SlotDraft[], nowMs: number): string[] {
  const heute = berlinDay(new Date(nowMs).toISOString());
  const jetzt = berlinTime(new Date(nowMs).toISOString());
  return rows.map((r, i) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.day) || !/^\d{2}:\d{2}$/.test(r.time)) return t("Datum und Uhrzeit angeben.", "Enter date and time.");
    if (r.day < heute || (r.day === heute && r.time <= jetzt)) return t("Liegt in der Vergangenheit.", "Lies in the past.");
    // K-556 d: am Tag der Zeitumstellung gibt es 02:00–02:59 nicht (die Datenbank verschob still auf 03:xx)
    if (!zeitGibtEs(r.day, r.time)) return t("Diese Uhrzeit gibt es an diesem Tag nicht (Zeitumstellung). Bitte eine andere wählen.", "This time does not exist on this day (clock change). Please choose another.");
    const link = r.link.trim();
    if (link && !/^https:\/\/[^\s"<>\\]+$/.test(link)) return t("Link muss mit https:// beginnen.", "Link must start with https://.");
    const [h, m] = r.time.split(":").map(Number) as [number, number];
    const a0 = h * 60 + m, a1 = a0 + r.minutes;
    const doppelt = rows.some((o, j) => {
      if (j === i || o.day !== r.day || !/^\d{2}:\d{2}$/.test(o.time)) return false;
      const [oh, om] = o.time.split(":").map(Number) as [number, number];
      const b0 = oh * 60 + om, b1 = b0 + o.minutes;
      return a0 < b1 && b0 < a1;
    });
    if (doppelt) return t("Überschneidet sich mit einer anderen Zeile.", "Overlaps another row.");
    return "";
  });
}

export function draftsToPayload(rows: readonly SlotDraft[]): { day: string; time: string; minutes: number; meeting_url: string | null }[] {
  return rows.map((r) => ({ day: r.day, time: r.time, minutes: r.minutes, meeting_url: r.link.trim() || null }));
}

/** K-556 d: Gibt es die Uhrzeit an diesem Tag in Europe/Berlin? (nein am Tag der Umstellung auf Sommerzeit, 02:00–02:59) */
export function zeitGibtEs(day: string, time: string): boolean {
  const basis = Date.parse(`${day}T${time}:00Z`);
  if (Number.isNaN(basis)) return false;
  return [60, 120].some((off) => {
    const iso = new Date(basis - off * 60_000).toISOString();
    return berlinDay(iso) === day && berlinTime(iso) === time;
  });
}

const minuten = (time: string) => { const [h, m] = time.split(":").map(Number) as [number, number]; return h * 60 + m; };
const hhmm = (n: number) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;

/**
 * K-556 b: Vorschlag für das nächste Zeitfenster: ab dem nächsten Werktag zwischen 10:00 und 17:00 im Halbstundentakt die erste
 * Zeit, die sich mit keinem bestehenden Zeitfenster (frei oder gebucht) und keiner schon eingetragenen Zeile überschneidet.
 */
export function naechsteFreieZeit(slots: readonly Pick<DemoSlot, "starts_at" | "duration_minutes" | "removed_at">[], nowMs: number, minutes = 30, extra: readonly SlotDraft[] = []): SlotDraft {
  const belegt = new Map<string, [number, number][]>();
  const merke = (day: string, a: number, b: number) => belegt.set(day, [...(belegt.get(day) ?? []), [a, b]]);
  for (const s of slots) if (!s.removed_at) { const a = minuten(berlinTime(s.starts_at)); merke(berlinDay(s.starts_at), a, a + s.duration_minutes); }
  for (const r of extra) if (/^\d{2}:\d{2}$/.test(r.time)) { const a = minuten(r.time); merke(r.day, a, a + r.minutes); }
  let day = nextWorkday(berlinDay(new Date(nowMs).toISOString()));
  for (let n = 0; n < 60; n++) {
    for (let a = 10 * 60; a + minutes <= 17 * 60 + 30; a += 30) {
      if (!(belegt.get(day) ?? []).some(([x, y]) => a < y && x < a + minutes) && zeitGibtEs(day, hhmm(a))) return { day, time: hhmm(a), minutes, link: "" };
    }
    day = nextWorkday(day);
  }
  return { ...firstDraft(nowMs), minutes };
}

/** K-556 g: Stand des Testkontos wie in der Betriebsmail: „Testphase bis 22.10.“, „Kunde“ oder der Status */
export function testkontoStand(o: DemoTestOrg, nowMs: number): string {
  if (o.trial_ends_at && Date.parse(o.trial_ends_at) > nowMs) {
    const bis = new Intl.DateTimeFormat(t("de-DE", "en-GB"), { timeZone: DEMO_ZONE, day: "2-digit", month: "2-digit" }).format(new Date(o.trial_ends_at));
    return t(`Testphase bis ${bis}`, `Trial until ${bis}`);
  }
  if (o.status === "active") return t("Kunde", "Customer");
  if (o.trial_ends_at) return t("Testphase abgelaufen", "Trial expired");
  return o.status;
}
